import { useEffect, useMemo, useState } from 'react';
import type { MusicVideoEditorResponse } from 'fym-shared/src/music24/musicVideoApi';
import { sectionCastFileIds } from './referenceDefaults';

/**
 * The references and the style of a new AI shot (directed video design i1 to i3, spec §9):
 *
 * - `directed`: the directed video of the open cut, or null. A cut that a directed video made opens
 *   with the cast that its shots in the section use most (max 3) and the style switch on. The
 *   picks follow the section until the user changes them.
 * - `hasLibrary`: the account has cast pictures. With none, the form is the form of today.
 * - `request`: the `referenceFileIds` and `styleText` fields of `POST /footage/ai`.
 */
export function useAiShotOptions(
  data: Pick<MusicVideoEditorResponse, 'directed' | 'castLibrary'>,
  cutUuid: string,
  section: { startSec: number; endSec: number } | null
) {
  const directed = data.directed !== null && data.directed.cutUuids.includes(cutUuid) ? data.directed : null;
  const hasLibrary = data.castLibrary.length > 0 || (directed !== null && directed.cast.length > 0);
  const sectionStart = section?.startSec ?? null;
  const sectionEnd = section?.endSec ?? null;
  const defaultFileIds = useMemo(
    () =>
      directed === null
        ? []
        : sectionCastFileIds(
            directed,
            sectionStart === null || sectionEnd === null ? null : { startSec: sectionStart, endSec: sectionEnd }
          ),
    [directed, sectionEnd, sectionStart]
  );
  const [fileIds, setFileIdsState] = useState<number[]>(defaultFileIds);
  const [isEdited, setIsEdited] = useState(false);
  useEffect(() => {
    if (!isEdited) {
      setFileIdsState(defaultFileIds);
    }
  }, [defaultFileIds, isEdited]);
  const [isStyleOn, setIsStyleOn] = useState(true);
  const [styleText, setStyleText] = useState('');

  const directedStyleText = directed?.styleText ?? '';
  const effectiveStyleText = (directed === null ? styleText : isStyleOn ? directedStyleText : '').trim();

  return {
    directed,
    hasLibrary,
    fileIds,
    setFileIds: (next: number[]) => {
      setFileIdsState(next);
      setIsEdited(true);
    },
    isStyleOn,
    toggleStyle: () => setIsStyleOn((on) => !on),
    styleText,
    setStyleText,
    effectiveStyleText,
    request: {
      ...(hasLibrary ? { referenceFileIds: fileIds } : {}),
      ...(effectiveStyleText === '' ? {} : { styleText: effectiveStyleText }),
    },
  };
}
